// PRD 06 §2.1 — one card per module. Header has icon + name + MID + response
// badge + frequency text. Body has the area-grouped table.
//
// Slice 7 wires:
//   - ✕ row remove → ConfirmModal → updateModule({ itemIds without pid,
//     pruned areaOrder }).
//   - `+ 항목 추가` button → AddItemPopover → updateModule({ itemIds + pid,
//     areaOrder }). The popover only offers unassigned items (ADM.006), so an
//     add is always a plain append — there is no cross-module move.

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { DirtyBadge } from '@/components/feedback/DirtyBadge';
import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

import { AddItemPopover } from './AddItemPopover';
import { AreaItemTable } from './AreaItemTable';
import { useUpdateModuleMutation } from '../hooks/use-deployment-mutations';
import { proDeploymentQueryKeys } from '../hooks/use-deployment-current-config';
import {
  buildModuleAreaBreakdown,
  moveAreaTo,
  moveItemTo,
  pruneAreaOrder,
  resolveModuleItems,
} from '../utils/area-group';
import { ProDeploymentApiError, type Module, type ProItemRef } from '../types/pro-deployment.types';

interface ModuleCardProps {
  module: Module;
  itemDict: ReadonlyMap<string, ProItemRef>;
  diseaseFilter: string;
  // FEATURE_025 — whether an item belongs to the selected dropdown row.
  isVisible: (item: ProItemRef) => boolean;
  // FEATURE_025 — whether a shown item is 조회 전용 on the selected row. Omitted → none is.
  isViewOnly?: (item: ProItemRef) => boolean;
}

export function ModuleCard({ module, itemDict, diseaseFilter, isVisible, isViewOnly }: ModuleCardProps) {
  const { t } = useTranslation('pro-deployment');
  // SHCT-582 — PRD 21 §7.4 P-2. 항목 추가 is `disabled` + 툴팁 without PRO_MGMT:WRITE instead of
  // vanishing; the row-level remove/reorder affordances below stay withheld, because a drag handle
  // has no disabled state to read and AreaItemTable already gives the ✕ the P-2 treatment.
  const { canWrite, denyProps } = useWriteGuard('PRO_MGMT');
  const updateModule = useUpdateModuleMutation();
  const queryClient = useQueryClient();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<ProItemRef | null>(null);
  // Opened when a write 409s on MODULE_VERSION_CONFLICT (another admin saved
  // first). Closing it refetches the snapshot so the FE picks up the new version.
  const [conflictOpen, setConflictOpen] = useState(false);
  // SHCT-617 — holds the server message when a write 404s on MODULE_NOT_FOUND
  // (the module was pruned as an orphan while this card was open). `null` = closed.
  const [notFoundMessage, setNotFoundMessage] = useState<string | null>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  // SHCT-728 — PRD 06 §3.4. The badge now reports what the SERVER decided, by comparing the current
  // configuration against the last publish snapshot.
  //
  // It used to be derived from the browser's dirty store, which is `sessionStorage` (PRD 21 §10.1) and
  // therefore could not answer the two 수용 기준 this ticket turns on: the mark has to survive closing
  // the tab, and it has to be the same mark for another operator. It also could only see dirty ITEMS —
  // a dirty 문항 is a QID and a dirty 관리지침 a GID, neither of which the client can attribute to a
  // module — and a module made stale by one of its questions is precisely the case that caused the
  // incident. The server sees all three.
  const moduleDirty = module.dirty ?? false;

  // PRD 06 §2.1/§3.3 — the header badge is the response-policy label (매일 필수
  // 응답 / 해당 주 내 자유 응답), served by BE in `responseLabel`. The item·question
  // count is not shown here (it lives in the table Total row).
  const badgeClass =
    module.responseLabelType === 'warning' ? 'badge badge-warning' : 'badge badge-primary';

  // FEATURE_025 — the module is shared by every dropdown row, so the table shows, numbers and counts only
  // the selected row's items. Every SAVE below still starts from the full `module` (all of its items and its
  // whole area order): building a save from this filtered copy would delete the other rows' items.
  const displayModule = useMemo(
    () => ({
      ...module,
      itemIds: module.itemIds.filter((pid) => {
        const item = itemDict.get(pid);
        return item ? isVisible(item) : false;
      }),
    }),
    [module, itemDict, isVisible],
  );

  // A concurrent-edit conflict is a state clash (Business Logic Error, §7.1) →
  // Modal, not a toast. Every other write error stays a toast.
  const handleWriteError = (toastKey: string) => (err: unknown) => {
    if (err instanceof ProDeploymentApiError && err.code === 'MODULE_VERSION_CONFLICT') {
      setConflictOpen(true);
      return;
    }
    // SHCT-617 — the server now prunes a module whose cadence has no matching PRO
    // item (PRD 06 §3.2), so MODULE_NOT_FOUND became reachable: another admin's
    // page load can delete this very card mid-edit. Also a Business Logic Error
    // (state clash) → Modal, not the generic "저장 실패" toast, which would name the
    // wrong cause. Body = the server's message (CLAUDE.md 에러 메시지 규칙 4 chain:
    // i18n key → server message), so no invented copy.
    if (err instanceof ProDeploymentApiError && err.code === 'MODULE_NOT_FOUND') {
      setNotFoundMessage(
        t('errors.MODULE_NOT_FOUND', { defaultValue: err.message }),
      );
      return;
    }
    toast.error(t(toastKey));
  };

  const handleAdd = (item: ProItemRef) => {
    setPopoverOpen(false);
    // Guard against a duplicate PID (one item ↔ one module, ADM.006) — the pool
    // already excludes assigned items, but never append a pid already present.
    if (module.itemIds.includes(item.pid)) return;
    const nextItemIds = [...module.itemIds, item.pid];
    updateModule.mutate(
      {
        mid: module.mid,
        input: {
          itemIds: nextItemIds,
          areaOrder: [...module.areaOrder],
          expectedVersion: module.version,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('addItem.toasts.added', { name: item.name }));
        },
        onError: handleWriteError('addItem.toasts.error'),
      },
    );
  };

  const handleReorderAreas = (sourceArea: string, targetArea: string, placeAfter: boolean) => {
    const sortedAreas = buildModuleAreaBreakdown(module, itemDict).groups.map((g) => g.name);
    const nextAreaOrder = moveAreaTo(sortedAreas, sourceArea, targetArea, placeAfter);
    updateModule.mutate(
      {
        mid: module.mid,
        input: {
          itemIds: [...module.itemIds],
          areaOrder: nextAreaOrder,
          expectedVersion: module.version,
        },
      },
      { onError: handleWriteError('module.reorderError') },
    );
  };

  const handleReorderItems = (sourceItemId: string, targetItemId: string, placeAfter: boolean) => {
    const nextItemIds = moveItemTo(module.itemIds, sourceItemId, targetItemId, placeAfter);
    updateModule.mutate(
      {
        mid: module.mid,
        input: {
          itemIds: nextItemIds,
          areaOrder: [...module.areaOrder],
          expectedVersion: module.version,
        },
      },
      { onError: handleWriteError('module.reorderError') },
    );
  };

  const handleRemoveConfirm = () => {
    if (!removeTarget) return;
    const target = removeTarget;
    const nextItemIds = module.itemIds.filter((id) => id !== target.pid);
    const nextItems = resolveModuleItems({ ...module, itemIds: nextItemIds }, itemDict);
    const nextAreaOrder = pruneAreaOrder(module.areaOrder, nextItems);
    updateModule.mutate(
      {
        mid: module.mid,
        input: {
          itemIds: nextItemIds,
          areaOrder: nextAreaOrder,
          expectedVersion: module.version,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('removeItem.toast', { name: target.name }));
        },
        onError: handleWriteError('removeItem.error'),
        onSettled: () => {
          setRemoveTarget(null);
        },
      },
    );
  };

  return (
    <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
      {/* Header */}
      <div
        style={{
          padding: 'var(--space-4) var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <span style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>
            {module.icon} {module.name}
          </span>
          <span
            style={{
              fontSize: 10,
              color: 'var(--muted-foreground)',
              fontWeight: 400,
            }}
          >
            {module.mid}
          </span>
          <span className={badgeClass}>{module.responseLabel}</span>
          {/* SHCT-728 — PRD 06 §2.2 No.2-1 「모듈 카드 미배포 배지」. Same wording source as the
              header chip and the PRD 04·05·07 item badges (SHCT-604, `common:badges.dirty`), so the
              three layers cannot drift into three different words for one state. */}
          {moduleDirty ? <DirtyBadge title={t('module.undeployed.tooltip')} /> : null}
        </div>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--muted-foreground)' }}>
          {module.frequencyLabel}
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: 'var(--space-4) var(--space-6)' }}>
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--muted-foreground)',
            marginBottom: 'var(--space-2)',
          }}
        >
          {t('module.containedItems')}
        </div>
        <AreaItemTable
          module={displayModule}
          itemDict={itemDict}
          canWrite={canWrite}
          onRequestRemove={setRemoveTarget}
          onReorderAreas={canWrite ? handleReorderAreas : null}
          onReorderItems={canWrite ? handleReorderItems : null}
          isViewOnly={isViewOnly}
        />

        <div
          style={{
            marginTop: 'var(--space-3)',
            position: 'relative',
            display: 'inline-flex',
          }}
        >
            <button
              ref={addButtonRef}
              type="button"
              onClick={() => setPopoverOpen((open) => !open)}
              disabled={!canWrite || updateModule.isPending}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                padding: '4px 10px',
                background: 'transparent',
                color: canWrite ? 'var(--info)' : 'var(--muted-foreground)',
                borderRadius: 9999,
                fontSize: 'var(--font-size-xs)',
                fontWeight: 500,
                border: `1px dashed ${canWrite ? 'var(--info)' : 'var(--border)'}`,
                cursor: canWrite ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
              }}
              {...denyProps}
            >
              {t('addItem.button')}
            </button>
            {popoverOpen ? (
              <AddItemPopover
                module={module}
                diseaseFilter={diseaseFilter}
                anchorRef={addButtonRef}
                onSelect={handleAdd}
                onClose={() => setPopoverOpen(false)}
              />
            ) : null}
        </div>
      </div>

      <ConfirmModal
        open={removeTarget !== null}
        title={t('removeItem.confirm.title')}
        message={t('removeItem.confirm.message', { name: removeTarget?.name ?? '' })}
        confirmLabel={t('removeItem.confirm.confirmLabel')}
        confirmVariant="destructive"
        loading={updateModule.isPending}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveTarget(null)}
      />

      <InfoModal
        open={conflictOpen}
        title={t('module.conflict.title')}
        body={t('module.conflict.body')}
        onClose={() => {
          setConflictOpen(false);
          // Refetch so the FE picks up the winning admin's state + new version.
          queryClient.invalidateQueries({ queryKey: proDeploymentQueryKeys.all });
        }}
      />

      {/* SHCT-617 — module pruned server-side mid-edit. Refetch on close so this
          now-deleted card disappears instead of lingering with stale state. */}
      <InfoModal
        open={notFoundMessage !== null}
        title={t('module.notFound.title')}
        body={notFoundMessage ?? ''}
        onClose={() => {
          setNotFoundMessage(null);
          queryClient.invalidateQueries({ queryKey: proDeploymentQueryKeys.all });
        }}
      />
    </div>
  );
}
